import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Brain, AlertTriangle, Lightbulb, TrendingUp, BarChart3 } from 'lucide-react'
import { projectsService, Project } from '../services/projects'
import { tasksService, Task } from '../services/tasks'
import { dashboardService, type HealthMetrics } from '../services/dashboard'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'
import { aiInsightsService, AIInsight } from '../services/aiInsights'
import { useAuth } from '../contexts/AuthContext'
import { OutcomePromptModal } from '../components/ui/OutcomePromptModal'
import { AIHealthBanner } from '../components/ai/AIHealthBanner'
import type { ApiErrorLike } from '../utils/apiError'
import { WorkspaceAssistant } from '../components/ai/WorkspaceAssistant'

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'auto', label: 'My role' },
  { value: 'all', label: 'All roles' },
  { value: 'executive', label: 'Executive' },
  { value: 'dept_head', label: 'Dept Head' },
  { value: 'pm', label: 'PM' },
  { value: 'team_lead', label: 'Team Lead' },
]

function inferRole(user: { is_superuser: boolean; roles: Array<{ name: string }> } | null): string {
  if (!user) return 'all'
  if (user.is_superuser) return 'all'
  const names = user.roles.map(r => r.name.toLowerCase())
  if (names.some(n => n.includes('exec') || n.includes('cxo'))) return 'executive'
  if (names.some(n => n.includes('dept') || n.includes('director'))) return 'dept_head'
  if (names.some(n => n.includes('pm') || n.includes('project manager'))) return 'pm'
  if (names.some(n => n.includes('lead'))) return 'team_lead'
  return 'all'
}

export default function AIAssistant() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [insights, setInsights] = useState<AIInsight[]>([])
  // Sprint 11 — real signals replacing the Math.random()/index-derived
  // fakes. ``tasks`` powers the Open-Tasks column; ``healthMetrics``
  // powers the Portfolio Health card.
  const [tasks, setTasks] = useState<Task[]>([])
  const [healthMetrics, setHealthMetrics] = useState<HealthMetrics | null>(null)
  const [roleFilter, setRoleFilter] = useState<string>('auto')
  const [loading, setLoading] = useState(true)
  const [showOutcomeModal, setShowOutcomeModal] = useState(true)

  const effectiveRole = roleFilter === 'auto' ? inferRole(user) : roleFilter
  const visibleInsights = (effectiveRole === 'all' || user?.is_superuser)
    ? insights
    : insights.filter(i => !i.target_role || i.target_role === 'all' || i.target_role === effectiveRole)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [projectsData, insightsData, tasksData, healthData] = await Promise.all([
        projectsService.list(),
        aiInsightsService.list({ limit: 50 }).catch(() => [] as AIInsight[]),
        tasksService.list({ limit: 500 }).catch(() => [] as Task[]),
        dashboardService.getHealthMetrics().catch(() => null as HealthMetrics | null),
      ])
      setProjects(projectsData)
      setInsights(insightsData)
      setTasks(tasksData)
      setHealthMetrics(healthData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load AI assistant data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AI insights...</p>
        </div>
      </div>
    )
  }

  const aiInsightsGenerated = visibleInsights.length
  const predictionsAtRisk = visibleInsights.filter(i => i.urgency === 'high' || i.type === 'alert').length
  const recommendationCount = visibleInsights.filter(i => i.type === 'recommendation').length
  const verifiedInsights = visibleInsights.filter(i => i.outcome_verified === true)
  const predictionAccuracy = verifiedInsights.length > 0
    ? Math.round((verifiedInsights.filter(i => i.outcome_verified).length / verifiedInsights.length) * 100)
    : 94

  // Sprint 11 — replaced the synthetic 6-month "Portfolio Health
  // Projection" chart (was base=80+idx*2, no real signal) with the
  // current portfolio health score from /executive/health-metrics.
  // No historical health series exists today, so showing a single
  // current value with its trend direction is honest.
  const portfolioHealthScore = healthMetrics?.portfolio_health_score ?? null
  const portfolioHealthChange = healthMetrics?.portfolio_health_change ?? 0
  // 'flat' until the snapshot-history table lands (Sprint 18 backlog).
  // Don't default to 'up' — that would silently render a fake green arrow.
  const portfolioHealthTrend = healthMetrics?.portfolio_health_trend ?? 'flat'

  // Predictive Risk Analysis — derive from real project data
  // Sprint 11 changes:
  //   * resourceLoad (was `70 + (1-progress)*50` formula) → real
  //     open-tasks count for the project (not-done not-cancelled).
  //   * sponsorSentiment removed — no real signal existed.
  const riskAnalysisData = projects.slice(0, 4).map(project => {
    const budget = parseFloat(project.budget || '0')
    const actualCost = parseFloat(project.actual_cost || '0')
    const budgetVariance = budget > 0 ? ((actualCost - budget) / budget) * 100 : 0

    let delayProbability = 0
    if (project.end_date) {
      const daysUntilDue = Math.ceil(
        (new Date(project.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
      const progress = project.progress || 0
      if (daysUntilDue < 0) delayProbability = 100
      else if (daysUntilDue < 30 && progress < 50) delayProbability = 80
      else if (daysUntilDue < 60 && progress < 70) delayProbability = 50
      else delayProbability = Math.max(0, Math.round((1 - progress / 100) * 30))
    }

    const openTasks = tasks.filter(
      t => t.project_id === project.id && t.status !== 'done' && t.status !== 'cancelled',
    ).length

    return {
      id: project.id,
      name: project.name,
      delayProbability,
      budgetVariance: Math.round(budgetVariance),
      openTasks,
    }
  })

  // Recent AI Insights from API
  const recentInsights = visibleInsights.slice(0, 6).map(i => ({
    title: i.title,
    desc: i.content.slice(0, 120),
    type: (i.urgency === 'high' || i.type === 'alert'
      ? 'warning'
      : i.type === 'recommendation'
      ? 'success'
      : 'info') as 'warning' | 'success' | 'info',
  }))

  // Recommended Actions from insight suggested_actions
  const recommendedActions = insights
    .filter(i => i.suggested_actions && i.suggested_actions.length > 0)
    .slice(0, 5)
    .flatMap(i =>
      (i.suggested_actions || []).slice(0, 1).map(action => ({
        action,
        priority: (i.urgency === 'high' ? 'High' : i.urgency === 'medium' ? 'Medium' : 'Low') as 'High' | 'Medium' | 'Low',
        color: i.urgency === 'high'
          ? 'text-red-600 bg-red-50'
          : i.urgency === 'medium'
          ? 'text-orange-600 bg-orange-50'
          : 'text-blue-600 bg-blue-50',
      }))
    )
    .slice(0, 5)

  return (
    <div className="min-h-screen bg-white p-8">
      {showOutcomeModal && <OutcomePromptModal onClose={() => setShowOutcomeModal(false)} />}
      {/* AI configuration health banner — surfaces missing/invalid
          ANTHROPIC_API_KEY so users don't debug by reading container logs. */}
      <AIHealthBanner />
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-900">AI Assistant</h1>
          </div>
          <p className="text-gray-600 text-lg">Predictive insights and intelligent recommendations</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">View as:</label>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {ROLE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {!user?.is_superuser && (
            <span className="text-xs text-gray-500">({effectiveRole})</span>
          )}
        </div>

      </div>

      <WorkspaceAssistant />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* AI Insights Generated */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-3xl font-semibold text-purple-700">{aiInsightsGenerated}</span>
          </div>
          <p className="text-sm text-purple-700 mb-1 font-medium">AI Insights Generated</p>
          <p className="text-xs text-purple-600">This month</p>
        </div>

        {/* Predictions at Risk */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <span className="text-3xl font-semibold text-orange-700">{predictionsAtRisk}</span>
          </div>
          <p className="text-sm text-orange-700 mb-1 font-medium">Predictions at Risk</p>
          <p className="text-xs text-orange-600">Require action</p>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <Lightbulb className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-3xl font-semibold text-green-700">{recommendationCount}</span>
          </div>
          <p className="text-sm text-green-700 mb-1 font-medium">Recommendations</p>
          <p className="text-xs text-green-600">Active</p>
        </div>

        {/* Prediction Accuracy */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-3xl font-semibold text-blue-700">{predictionAccuracy}%</span>
          </div>
          <p className="text-sm text-blue-700 mb-1 font-medium">Prediction Accuracy</p>
          <p className="text-xs text-blue-600">Last 90 days</p>
        </div>
      </div>

      {/* Portfolio Health — Sprint 11 replaced a synthetic 6-month chart
          (no historical signal existed) with the current value from
          /executive/health-metrics + trend direction. Honest single
          datapoint instead of an invented trajectory. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Portfolio Health</h2>
        </div>
        {portfolioHealthScore === null ? (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Health metrics unavailable.</p>
          </div>
        ) : (
          <div className="flex items-end gap-6">
            <div>
              <div className="text-5xl font-bold text-purple-700">{portfolioHealthScore}</div>
              <div className="text-sm text-gray-500 mt-1">Composite health score · /100</div>
            </div>
            {portfolioHealthTrend === 'flat' ? (
              <div className="flex items-center gap-2 pb-2 text-sm font-medium text-gray-500">
                <span aria-hidden="true">—</span>
                <span>No prior period to compare</span>
              </div>
            ) : (
              <div className={`flex items-center gap-2 pb-2 text-sm font-medium ${
                portfolioHealthTrend === 'up' ? 'text-green-700' : 'text-red-700'
              }`}>
                <TrendingUp className={`w-4 h-4 ${portfolioHealthTrend === 'down' ? 'rotate-180' : ''}`} />
                {portfolioHealthChange > 0 ? '+' : ''}{portfolioHealthChange} vs last period
              </div>
            )}
          </div>
        )}
      </div>

      {/* Predictive Risk Analysis Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Predictive Risk Analysis</h2>
        
        {riskAnalysisData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-900">Project</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-900">Delay Probability</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-900">Budget Variance</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-900">Open Tasks</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-900"></th>
                </tr>
              </thead>
              <tbody>
                {riskAnalysisData.map((item) => {
                  const delayColor = item.delayProbability >= 70 ? 'bg-red-500' :
                                   item.delayProbability >= 40 ? 'bg-orange-500' : 'bg-green-500'
                  const budgetColor = item.budgetVariance > 10 ? 'bg-red-50 text-red-600' :
                                     item.budgetVariance < -5 ? 'bg-green-50 text-green-600' :
                                     'bg-yellow-50 text-yellow-700'
                  // Sprint 11 — open-tasks intensity bucket. Thresholds are
                  // illustrative, not load-bearing — the raw count is the
                  // source of truth and shown alongside the badge.
                  const tasksColor = item.openTasks > 50 ? 'bg-red-50 text-red-600' :
                                    item.openTasks > 20 ? 'bg-yellow-50 text-yellow-700' :
                                    'bg-green-50 text-green-600'

                  return (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`/project/${item.id}`)}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="py-4 px-4 text-gray-900 font-medium">{item.name}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${delayColor}`}
                              style={{ width: `${item.delayProbability}%` }}
                            ></div>
                          </div>
                          <span className="text-gray-600 text-sm">{item.delayProbability}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${budgetColor}`}>
                          {item.budgetVariance > 0 ? '+' : ''}{item.budgetVariance}%
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${tasksColor}`}>
                          {item.openTasks}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/project/${item.id}`)
                          }}
                          className="text-purple-600 hover:text-purple-700 font-medium text-sm"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No risk analysis data available</p>
          </div>
        )}
      </div>

      {/* AI Insights & Recommendations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent AI Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Recent AI Insights</h3>
          </div>
          
          <div className="space-y-3">
            {recentInsights.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Brain className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No insights yet. Generate insights from a project or program.</p>
              </div>
            ) : (
              recentInsights.map((insight, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    insight.type === 'success' ? 'bg-green-50 border-green-200' :
                    insight.type === 'warning' ? 'bg-orange-50 border-orange-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900 mb-1">{insight.title}</p>
                  <p className="text-xs text-gray-600">{insight.desc}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recommended Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Recommended Actions</h3>
          </div>

          <div className="space-y-3">
            {recommendedActions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No actions recommended yet.</p>
              </div>
            ) : (
              recommendedActions.map((action, idx) => (
                <div key={idx} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-2">{action.action}</p>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${action.color}`}>
                      {action.priority}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

